import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CloudLightning, Play, ShieldWarning } from '@phosphor-icons/react';
import API_URL from '../config';

const Flags = ({ r }) => (
  <>
    {r.merged_ticket && <span className="ml-1 text-[10px] font-bold px-1" style={{ background: '#FEF3C7', color: '#92400E' }}>MERGED TICKET</span>}
    {r.recently_added && <span className="ml-1 text-[10px] font-bold px-1" style={{ background: '#E0E7FF', color: '#3730A3' }}>RECENTLY ADDED</span>}
  </>
);

function Rows({ id, title, rows, render }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-3" data-testid={`tier-audit-${id}`}>
      <p className="text-sm font-semibold" style={{ color: '#141b4d' }}>{title} ({rows.length})</p>
      <ul className="text-sm space-y-1 mt-1 max-h-72 overflow-auto">{rows.map(render)}</ul>
    </div>
  );
}

export const ReportTierAuditPanel = () => {
  const [st, setSt] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const timer = useRef(null);

  const poll = async (since = null) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/report-tier-audit/status`);
      setSt(res.data);
      const stale = since && (!res.data.started_at || new Date(res.data.started_at) < new Date(since));
      if (res.data.running || stale) timer.current = setTimeout(() => poll(since), 1500);
    } catch (e) { /* best-effort */ }
  };
  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (apply) => {
    setConfirming(false);
    try {
      const res = await axios.post(`${API_URL}/api/admin/report-tier-audit`, { apply });
      clearTimeout(timer.current);
      poll(res.data.started_at || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start the audit'); }
  };

  const r = st?.result;
  const running = st?.running;
  const n = r?.counts;
  return (
    <div className="card-swiss mb-6" data-testid="tier-audit-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="max-w-2xl">
          <p className="font-bold" style={{ color: '#141b4d' }}>Search-report rating audit</p>
          <p className="text-sm mt-1" style={{ color: '#52525B' }}>
            Re-reads each published report's "Risk Level:" from the linked client-ready SRL and lists matters whose displayed
            rating differs. Nothing is changed in HubSpot or the SRL Console, and no client is notified.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => start(false)} disabled={running} data-testid="tier-audit-dryrun-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold disabled:opacity-60" style={{ background: '#141b4d', color: '#fff' }}>
            <CloudLightning weight="bold" className="w-4 h-4" /> {running ? `Checking ${st.done}/${st.total || '…'}` : 'Dry run'}
          </button>
          {r && !r.apply && !running && n.mismatches > 0 && !confirming && (
            <button onClick={() => setConfirming(true)} data-testid="tier-audit-apply-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold" style={{ background: '#16A34A', color: '#fff' }}>
              <Play weight="bold" className="w-4 h-4" /> Correct {n.mismatches}
            </button>
          )}
          {confirming && !running && (
            <div className="flex items-center gap-2" data-testid="tier-audit-confirm">
              <span className="text-sm" style={{ color: '#52525B' }}>Set {n.mismatches} rating(s) to match the report? No client is notified.</span>
              <button onClick={() => start(true)} data-testid="tier-audit-confirm-btn"
                className="px-3 py-2 text-sm font-bold" style={{ background: '#16A34A', color: '#fff' }}>Yes, correct</button>
              <button onClick={() => setConfirming(false)} data-testid="tier-audit-cancel-btn"
                className="px-3 py-2 text-sm font-semibold border border-[#E4E4E7]">Cancel</button>
            </div>
          )}
        </div>
      </div>
      {st?.error && !running && <p className="mt-3 text-sm text-red-700" data-testid="tier-audit-error">Audit failed: {st.error}</p>}
      {r && !running && (
        <div className="mt-4 border border-[#E4E4E7] px-4 py-3" data-testid={r.apply ? 'tier-audit-receipt' : 'tier-audit-report'}>
          <p className="text-sm font-semibold" style={{ color: '#141b4d' }} data-testid="tier-audit-summary">
            {r.apply ? `Corrected ${n.corrected}.` : 'Dry run — nothing changed.'} {n.total} published report(s): {n.matched} match,
            {' '}{n.mismatches} mismatch, {n.no_rating} without a readable rating, {n.unreadable} unreadable.
          </p>
          {n.unreadable === n.total && n.total > 0 && (
            <p className="mt-2 flex items-center gap-2 text-sm font-semibold px-3 py-2" style={{ background: '#FEF3C7', color: '#92400E' }} data-testid="tier-audit-drive-warning">
              <ShieldWarning weight="fill" className="w-4 h-4" /> No report could be opened — Google Drive looks disconnected. Reconnect it and run again.
            </p>
          )}
          <Rows id="mismatches" title="Displayed rating differs from the report" rows={r.mismatches} render={(x) => (
            <li key={x.matter_id}>
              <span className="font-semibold">{x.client}</span> — {x.trademark_name}: shows <b>{x.stored_tier || 'none'}</b>, report says <b>{x.report_tier}</b>
              <span style={{ color: '#71717A' }}> ({x.source === 'report_text' ? 'from report text' : 'from filename'}{x.filename_tier && x.text_tier && x.filename_tier !== x.text_tier ? `; filename says ${x.filename_tier}` : ''})</span>
              <Flags r={x} />
            </li>
          )} />
          <Rows id="no-rating" title="No rating found in report or filename" rows={r.no_rating} render={(x) => (
            <li key={x.matter_id}>{x.client} — {x.trademark_name} <span style={{ color: '#71717A' }}>(shows {x.stored_tier || 'none'})</span><Flags r={x} /></li>
          )} />
          <Rows id="unreadable" title="Report couldn't be opened" rows={r.unreadable} render={(x) => (
            <li key={x.matter_id}>{x.client} — {x.trademark_name} <span style={{ color: '#71717A' }}>(shows {x.stored_tier || 'none'})</span><Flags r={x} /></li>
          )} />
        </div>
      )}
    </div>
  );
};
